import React, { useEffect, useState } from 'react';
import {
  COLORMAPS, bandStatRows, colormapGradient, initialRasterStyle, isRasterDataset, rasterInfoRows, rasterJobState,
} from '../lib/raster-api';
import DemPanel from './terrain/DemPanel';

/**
 * Raster dataset summary (datasets.metadata.raster written by the raster_ingest job,
 * docs/RASTER_SUPPORT.md): processing state, info, band statistics, and the colormap /
 * value range the map opens with. Renders nothing for a non-raster dataset.
 *
 * Props: dataset, job (the live ingest job from useJob, optional), onShowOnMap(style),
 * onProcess({ crs? }) to (re)queue the ingest job, processing (bool), onChanged() to reload
 * the dataset after a DEM change (components/terrain/DemPanel, docs/DEM_TERRAIN.md).
 */
function RasterCard({ dataset, job, onShowOnMap, onProcess, processing = false, onChanged }) {
  const meta = dataset?.metadata;
  const raster = meta?.raster;
  const [style, setStyle] = useState(() => initialRasterStyle(raster));
  const [crs, setCrs] = useState('');
  useEffect(() => { setStyle(initialRasterStyle(raster)); }, [raster]);
  if (!isRasterDataset(dataset)) return null;

  const state = rasterJobState(meta);
  const live = job && (job.status === 'queued' || job.status === 'running');
  const busy = processing || live || state.state === 'processing';
  const single = (style.bands || []).length === 1;
  const setNum = (k) => (e) => setStyle((s) => ({ ...s, [k]: e.target.value === '' ? null : e.target.value }));
  const stats = bandStatRows(raster);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="raster-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Raster</h2>
        <div className="flex gap-2">
          {raster && onShowOnMap && (
            <button onClick={() => onShowOnMap(single ? style : {})} data-testid="raster-show-on-map"
              className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted">
              Show on map
            </button>
          )}
          {onProcess && (
            <button onClick={() => onProcess(crs ? { crs } : {})} disabled={busy} data-testid="raster-process"
              className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50">
              {busy ? 'Processing…' : raster ? 'Reprocess' : 'Process raster'}
            </button>
          )}
        </div>
      </div>

      {busy && (
        <p className="text-sm text-muted-foreground mb-3" data-testid="raster-processing">
          {job?.message || 'Converting to a Cloud-Optimized GeoTIFF'}
          {job?.status === 'running' && job.progress > 0 && ` (${Math.round(job.progress * 100)}%)`}
        </p>
      )}
      {!busy && (state.state === 'failed' || state.state === 'unavailable') && (
        <p className="text-xs text-red-800 bg-red-100 rounded p-3 mb-3" role="alert" data-testid="raster-error">
          {state.error || 'Raster processing failed'}
        </p>
      )}
      {!raster && !busy && (
        <div className="flex items-center gap-2 mb-3 text-sm">
          <label htmlFor="raster-crs" className="text-muted-foreground">CRS override</label>
          <input id="raster-crs" value={crs} onChange={(e) => setCrs(e.target.value)} placeholder="e.g. EPSG:32614"
            className="px-2 py-1 text-xs border border-border rounded-md bg-background" />
        </div>
      )}

      {raster && (
        <>
          <dl className="grid grid-cols-2 gap-4">
            {rasterInfoRows(raster).map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm font-medium text-muted-foreground mb-1">{label}</dt>
                <dd className="text-sm font-semibold break-words">{value}</dd>
              </div>
            ))}
          </dl>

          {stats.length > 0 && (
            <table className="w-full mt-4 text-xs" data-testid="raster-band-stats">
              <thead>
                <tr className="text-muted-foreground text-left">
                  <th className="py-1">Band</th><th>Min</th><th>Max</th><th>Mean</th><th>P2</th><th>P98</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((b) => (
                  <tr key={b.band} className="border-t border-border">
                    <td className="py-1">{b.label}</td><td>{b.min}</td><td>{b.max}</td><td>{b.mean}</td><td>{b.p2}</td><td>{b.p98}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {single ? (
            <div className="mt-4 space-y-2" data-testid="raster-style">
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor="raster-colormap" className="text-muted-foreground w-20">Colormap</label>
                <select id="raster-colormap" value={style.colormap || 'viridis'}
                  onChange={(e) => setStyle((s) => ({ ...s, colormap: e.target.value }))}
                  className="px-2 py-1 text-xs border border-border rounded-md bg-background">
                  {COLORMAPS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="h-3 rounded" style={{ background: colormapGradient(style.colormap) }} data-testid="raster-ramp" />
              <div className="flex items-center gap-2 text-sm">
                <label className="text-muted-foreground w-20" htmlFor="raster-vmin">Range</label>
                <input id="raster-vmin" aria-label="Minimum" value={style.vmin ?? ''} onChange={setNum('vmin')}
                  className="w-24 px-2 py-1 text-xs border border-border rounded-md bg-background" />
                <span>to</span>
                <input aria-label="Maximum" value={style.vmax ?? ''} onChange={setNum('vmax')}
                  className="w-24 px-2 py-1 text-xs border border-border rounded-md bg-background" />
              </div>
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">Shown as RGB (bands {style.bands.join(', ')}).</p>
          )}
          <DemPanel dataset={dataset} onShowOnMap={onShowOnMap} onChanged={onChanged} />
        </>
      )}
    </div>
  );
}

export default RasterCard;
